import { gql } from '@apollo/client';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  Input,
  VStack,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import { useConfigUpdateBookmarksMutation } from './__generated__/ConfigWebPages.graphql';

// Taken from Yup since Zod doesn't do this regex based.
// https://github.com/jquense/yup/blob/master/src/string.ts#L23
const URL_REGEX =
  // eslint-disable-next-line
  /^((https?):)?\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

const bookmarkSchema = z.string().superRefine((val, ctx) => {
  if (val === '') return;
  if (!isEmpty(val) && !URL_REGEX.test(val)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t('invalidUrl'),
    });
  }
});

const schema = z.object({
  bookmark1: bookmarkSchema,
  bookmark2: bookmarkSchema,
  bookmark3: bookmarkSchema,
  bookmark4: bookmarkSchema,
  bookmark5: bookmarkSchema,
  bookmark6: bookmarkSchema,
  bookmark7: bookmarkSchema,
});

export type UpdateBookmarkFormValues = z.infer<typeof schema>;

export function ConfigWebPagesForm({
  configurationId,
  defaultBookmarks,
  configStatus,
}: {
  configurationId: string;
  defaultBookmarks: Array<string | undefined | null>;
  configStatus?: ConfigurationStatusType;
}) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const [configUpdateBookmarks] = useConfigUpdateBookmarksMutation();
  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);
  const BOOKMARKS = [
    'bookmark1',
    'bookmark2',
    'bookmark3',
    'bookmark4',
    'bookmark5',
    'bookmark6',
    'bookmark7',
  ] as const;

  const makeInitialState = (
    defaultBookmarks: Array<string | undefined | null>,
  ): UpdateBookmarkFormValues => ({
    bookmark1: defaultBookmarks[0] ?? '',
    bookmark2: defaultBookmarks[1] ?? '',
    bookmark3: defaultBookmarks[2] ?? '',
    bookmark4: defaultBookmarks[3] ?? '',
    bookmark5: defaultBookmarks[4] ?? '',
    bookmark6: defaultBookmarks[5] ?? '',
    bookmark7: defaultBookmarks[6] ?? '',
  });

  const initialState = makeInitialState(defaultBookmarks);

  const lastSubmitted = useRef(initialState);

  const {
    register,
    formState: { errors },
    getValues,
    trigger,
    reset,
  } = useForm<UpdateBookmarkFormValues>({
    defaultValues: initialState,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const newInitial = makeInitialState(defaultBookmarks);
    reset(newInitial);
    lastSubmitted.current = newInitial;
  }, [defaultBookmarks, reset]);

  const handleBlur = async () => {
    const values = getValues();
    const isValid = await trigger();
    if (!isValid) return;
    if (isEqual(values, lastSubmitted.current)) return;

    try {
      const { data } = await configUpdateBookmarks({
        variables: {
          input: {
            configurationId,
            bookmarks: Object.values(values),
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });

      if (!data?.configurationUpdateBookmarksAll?.id) {
        throw new Error('Update web pages failed');
      }

      lastSubmitted.current = values;
    } catch (err) {
      toast({
        status: 'error',
        title: t('updateWebPagesErr'),
        description: fromError(err, 'UpdateBookmarks'),
      });
    }
  };

  return (
    <GridItem colSpan={{ base: 8, md: 5 }}>
      <VStack spacing="3">
        {BOOKMARKS.map((bookmark, i) => {
          const { ref, ...bookmarkProps } = register(bookmark);

          return (
            <FormControl isInvalid={Boolean(errors[bookmark])} key={bookmark}>
              <FormLabel marginBottom="1">
                {t('webPage')} {i + 1}
              </FormLabel>
              <Input
                placeholder={t('webpagePlaceholder')}
                {...bookmarkProps}
                ref={ref}
                isDisabled={isDisplaySettingsUpdateDisabled}
                onBlur={handleBlur}
              />
              <FormErrorMessage>{errors[bookmark]?.message}</FormErrorMessage>
            </FormControl>
          );
        })}
      </VStack>
    </GridItem>
  );
}

ConfigWebPagesForm.graphql = {
  fragments: {
    ConfigBookmarks_config: gql`
      fragment ConfigBookmarks_config on ConfigurationModel {
        id
        bookmarks {
          all {
            reported
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateBookmarks: gql`
      mutation ConfigUpdateBookmarks($input: ConfigurationUpdateBookmarksAllInput!) {
        configurationUpdateBookmarksAll(input: $input) {
          id
          ...ConfigBookmarks_config
        }
      }
    `,
  },
};
